import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { campaignDefinition } from '../../../../../../entity-definitions/campaign.definition';
import { EntityDialogService } from '../../../../../../services/entity-dialog.service';
import { ToastService } from '../../../../../../services/toast.service';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { CampaignModel } from '../../../../../../types/campaign.interface';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { FieldConfig } from '../../../../../../types/entity-definition.interface';
import { isEntityFormDeleteAction } from '../../../../../../types/entity-form-result.interface';
import { isConflict } from '../../../../../../utils/http-error.utils';
import { CampaignDataService } from './campaign-data.service';
import { CampaignNavigationService } from './campaign-navigation.service';
import { campaignMinuteTakenValidator } from './campaign-timestamp.validator';

@Injectable({
  providedIn: 'root',
})
export class CampaignCreationService {
  private readonly dialogService = inject(EntityDialogService);
  private readonly toastService = inject(ToastService);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly campaignStore = inject(CampaignStore);
  private readonly data = inject(CampaignDataService);
  private readonly nav = inject(CampaignNavigationService);
  private readonly translateService = inject(TranslocoService);

  async loadCampaignModels(assetId: number): Promise<void> {
    await this.campaignModelStore.getByAssetId(assetId);
  }

  async createCampaign(): Promise<Campaign | null> {
    try {
      const assetId = this.nav.currentAssetId();
      if (!assetId) return null;

      const models = this.campaignModelStore.entities();
      const modelOptions = models.map((m: CampaignModel) => ({ name: m.name, id: m.id }));

      // Pre-emptive uniqueness check: the timestamp field validates against the asset's already
      // loaded campaigns so a colliding minute surfaces an inline error and greys Valider
      // synchronously — no async round-trip, so the button never flashes or needs a second click.
      // The backend 409 guard stays authoritative for collisions outside the loaded open slice.
      const baseFields = (campaignDefinition.fields ?? [])
        .filter(v => ['timestamp', 'operator', 'comment'].includes(v.field))
        .map(field =>
          field.field === 'timestamp'
            ? {
                ...field,
                validator: campaignMinuteTakenValidator(() => this.campaignStore.filterByAssetId(assetId)),
                uniqueErrorKey: 'entities.campaign.messages.duplicateTimestamp',
              }
            : field,
        );

      const hasModels = modelOptions.length > 0;
      const modelField: FieldConfig = {
        field: 'modelId',
        label: this.translateService.translate('common.labels.fromModel'),
        required: false,
        type: 'select',
        values: modelOptions,
        disabled: !hasModels,
        placeholder: hasModels
          ? this.translateService.translate('common.messages.selectModelOptional')
          : this.translateService.translate('common.messages.noModelAvailable'),
      };

      const fields = [...baseFields, modelField];

      const lightCampaignDefinition = {
        ...campaignDefinition,
        fields,
      };

      const defaultDate = new Date();
      defaultDate.setHours(0, 0, 0, 0);

      const formData = await lastValueFrom(
        this.dialogService.openCreateDialog<Campaign>({
          header: campaignDefinition.messages.dialogTitles.create(),
          entityDefinition: lightCampaignDefinition,
          mode: 'create',
          additionalData: {
            timestamp: defaultDate,
          },
        }),
      );

      if (!formData || isEntityFormDeleteAction(formData)) return null;

      const selectedModelId = formData['modelId'] as number | undefined;
      const hasModel = typeof selectedModelId === 'number' && selectedModelId > 0;

      const campaignData: Omit<Campaign, 'id'> = {
        name: '',
        timestamp: formData['timestamp'] as string,
        operator: formData['operator'] as string,
        comment: formData['comment'] as string,
        assetId,
        status: CampaignStatus.ON_GOING,
        isModel: false,
      };

      const campaign = await this.data.createCampaign(campaignData, hasModel ? selectedModelId : undefined);

      if (hasModel) {
        const model = models.find((m: CampaignModel) => m.id === selectedModelId);
        this.toastService.success('entities.campaign.messages.createdWithModel', undefined, undefined, undefined, {
          name: model?.name ?? this.translateService.translate('common.labels.model'),
        });
      } else {
        this.toastService.success('entities.campaign.messages.createdSuccess');
      }

      return campaign;
    } catch (err) {
      // A 409 timestamp collision already surfaces a localized toast via HttpErrorInterceptor;
      // skip the generic one to avoid a duplicate notification.
      if (!isConflict(err)) {
        this.toastService.error('entities.campaign.messages.createError');
      }
      return null;
    }
  }
}
